import React, { useState, useMemo, useCallback, useContext, useRef, useEffect, Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import { v4 } from 'uuid'

// assets
import Plus from 'Assets/images/plus.svg'

// contexts
import { StepContext, useFlowContext, BotContext } from '../contexts'
// store: selectors
import { getStepById, getActiveFlow } from 'Selectors/flow'
import { getAccountFeatures, getApps, getAccountDetails } from 'Selectors/account'
// store: actions
import { addAction } from 'Actions/flow'
import GuideEdit from './actions/edit/GuideEdit'
import AnswersEdit from './actions/edit/AnswerEdit'
import MessageEdit from './actions/edit/MessageEdit'
import PreChatForm from './actions/edit/PrechatForm'
import QuickReplyEdit from './actions/edit/QuickReplyEdit'
import TransferToDept from './actions/edit/TransferChatDept'
import ZapierTriggerEdit from './actions/edit/ZapierTriggerEdit'
import HappyFoxWorkflowEdit from './actions/edit/HappyFoxWorkflowEdit'
import CollectInformation from './actions/edit/CollectInformationEdit'
import MlCollectUserQuestion from './actions/edit/MlCollectUserQuestion'
import HfcCollectInformation from './actions/edit/HfcCollectInformationEdit'
import { getEscalateStep, getMlActionPayload } from './actions/edit/bot-response-setting-helper'

// others
import useKeyPress from 'LegacyUtils/hooks/useKeyPress'
import useOutsideClickListener from 'LegacyUtils/hooks/useOutsideClickListener'

const HFC_CHAT_WIDGET = 'HappyFox Chat Widget'
const BOT_WIDGET = 'Bot Widget'

const editors = {
  message: MessageEdit,
  quickReplies: QuickReplyEdit,
  collect_user_question: MlCollectUserQuestion,
  guide: GuideEdit,
  answer: AnswersEdit,
  collectInformation: HfcCollectInformation,
  botCollectInformation: CollectInformation,
  happyfoxchat_contact_details_form: PreChatForm,
  happyfoxchat_transfer_to_department: TransferToDept,
  happyfox_workflow_action: HappyFoxWorkflowEdit,
  zapier_trigger_form_submit: ZapierTriggerEdit,
  zapier_trigger_quick_reply_submit: ZapierTriggerEdit
}

const AllowedStartStepOptions = ['message', 'quickReplies', 'botCollectInformation', 'collect_user_question']

const allowedActionsForBotWidget = [
  'message',
  'quickReplies',
  'guide',
  'answer',
  'botCollectInformation',
  'mlSearch',
  'endChat'
]

const ListItem = ({ children, ...props }) => {
  return (
    <li className='options-list__item' {...props}>
      <Plus /> {children}
    </li>
  )
}

const options = [
  {
    key: 'message',
    featureKey: 'message',
    label: 'Bot Message',
    allowedWidgets: [BOT_WIDGET, HFC_CHAT_WIDGET]
  },
  {
    key: 'quickReplies',
    featureKey: 'quickReplies',
    label: 'User Choice',
    allowedWidgets: [BOT_WIDGET, HFC_CHAT_WIDGET],
    blocking: true
  },
  {
    key: 'collect_user_question',
    featureKey: 'mlSearch',
    label: 'Collect User Question',
    allowedWidgets: [BOT_WIDGET, HFC_CHAT_WIDGET],
    blocking: true
  },
  {
    key: 'guide',
    featureKey: 'guide',
    label: 'Show Guide',
    allowedWidgets: [BOT_WIDGET]
  },
  {
    key: 'answer',
    featureKey: 'answer',
    label: 'Show Answer',
    allowedWidgets: [BOT_WIDGET]
  },
  {
    key: 'botCollectInformation',
    featureKey: 'botCollectInformation',
    label: 'Collect Information',
    allowedWidgets: [BOT_WIDGET],
    blocking: true
  },
  {
    key: 'collectInformation',
    featureKey: 'collectInformation',
    label: 'Collect information (HFC)',
    allowedWidgets: [HFC_CHAT_WIDGET],
    blocking: true
  },
  {
    key: 'happyfoxchat_contact_details_form',
    featureKey: 'hfPrechatForm',
    label: 'PreChat Form (HFC)',
    allowedWidgets: [HFC_CHAT_WIDGET],
    blocking: true
  },
  {
    key: 'happyfoxchat_transfer_to_department',
    featureKey: 'hfTransferChatToDept',
    label: 'Transfer to Dept. (HFC)',
    allowedWidgets: [HFC_CHAT_WIDGET],
    blocking: true
  },
  {
    key: 'happyfox_workflow_action',
    label: 'Trigger HappyFox Workflow',
    integrationKey: 'happyfox_workflows',
    allowedWidgets: [BOT_WIDGET, HFC_CHAT_WIDGET],
    action: true,
    hasSettings: true
  },
  {
    key: 'zapier_trigger_form_submit',
    label: 'Zapier Trigger: Form Submit',
    integrationKey: 'zapier',
    allowedWidgets: [BOT_WIDGET],
    action: true,
    hasSettings: true
  },
  {
    key: 'zapier_trigger_quick_reply_submit',
    label: 'Zapier Trigger: Quick Reply Submit',
    integrationKey: 'zapier',
    allowedWidgets: [BOT_WIDGET],
    action: true,
    hasSettings: true
  },
  {
    key: 'end_chat',
    featureKey: 'endChat',
    label: 'End Conversation',
    allowedWidgets: [BOT_WIDGET, HFC_CHAT_WIDGET],
    action: true
  }
]

const Options = () => {
  const { stepId } = useContext(StepContext)
  const { widget_type } = useContext(BotContext)

  const [createMode, setCreateMode] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const accountFeatures = useSelector(getAccountFeatures)
  const step = useSelector((state) => getStepById(state, stepId))
  const apps = useSelector(getApps)

  const { steps } = useFlowContext()
  const activeFlow = useSelector(getActiveFlow)

  const dispatch = useDispatch()

  // message/quickReplies etc
  const [createActionType, setCreateActionType] = useState('')

  const hideMenu = useCallback(() => setShowMenu(false), [setShowMenu])
  const onAddButtonClick = () => {
    setShowMenu(true)
  }

  const accountData = useSelector(getAccountDetails)

  useEffect(() => {
    if (menuRef.current) {
      menuRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [showMenu])

  const menuRef = useRef()
  const closeOptionsMenu = useCallback(() => hideMenu(), [hideMenu])
  useOutsideClickListener(menuRef, closeOptionsMenu, showMenu)
  useKeyPress('Escape', closeOptionsMenu, showMenu)

  const onMenuClick = (option) => {
    hideMenu()

    if (option.action && !option.hasSettings) {
      return onAddActionClick(option.key)
    }
    if (option?.featureKey?.toLowerCase() === 'mlsearch') {
      const escalateStep = getEscalateStep({ ...steps })
      const botResponseSettings = activeFlow?.settings?.botResponseSettings ?? null
      const payload = getMlActionPayload({ ...botResponseSettings }, escalateStep, stepId, undefined, accountData)
      dispatch(addAction(payload))
      setCreateMode(false)
    } else {
      setCreateMode(true)
    }

    setCreateActionType(option.key)
  }

  const onCancelClick = useCallback(() => {
    setCreateMode(false)
    setCreateActionType('')
  }, [setCreateMode])

  const defaultOptions = useMemo(
    () => ({
      mode: 'create',
      createActionType,
      cancelFn: onCancelClick
    }),
    [onCancelClick, createActionType]
  )

  const Editor = editors[createActionType] || _.noop

  const onAddActionClick = useCallback(
    (type) => {
      onCancelClick()

      dispatch(
        addAction({
          stepId,
          actionId: v4(),
          setting: {
            type
          }
        })
      )
    },
    [dispatch, stepId, onCancelClick]
  )

  const canShowOptionByFeature = useCallback(
    (optionName) => {
      if (!optionName) {
        return true
      }

      const optionsFlags = _.get(accountFeatures, 'flowBuilder.options', {})

      if (widget_type && widget_type === HFC_CHAT_WIDGET) {
        return optionsFlags[optionName] ?? true
      }

      // If bot_widget is true, consider allowedActionsForBotWidget along with account level feature flag if any
      return optionsFlags[optionName] ?? allowedActionsForBotWidget.includes(optionName)
    },
    [accountFeatures, widget_type]
  )

  const isOptionAllowed = useCallback(
    (option) => {
      const isStartingStep = step.internal_name === 'start'

      // allow only certain actions in starting step
      if (isStartingStep) {
        return AllowedStartStepOptions.includes(option.key)
      }

      return true
    },
    [step.internal_name]
  )

  const canShowOption = useCallback(
    (option) => {
      const { key } = option

      const actionTypes = step.actions.map((id) => step.settings[id].type)
      const lastActionType = actionTypes[actionTypes.length - 1]

      // don't check for anything if last action is end_chat
      // there is nothing to do
      if (
        lastActionType === 'end_chat' ||
        lastActionType === 'collect_information' ||
        lastActionType === 'collect_information_email'
      ) {
        return false
      }

      // check if option is expecting any action to be added first
      if (option.lastActionType) {
        const lastUsedOption = options.find((o) => o.key === lastActionType)
        return option.lastActionType.includes(lastUsedOption?.key)
      }

      // allow adding quick replies after quick replies
      // also allow message after quick replies
      if (lastActionType === 'quickReplies' && (key === lastActionType || key === 'message')) {
        return true
      }

      // simple action items can be allowed anywehre
      // but make sure two consecutive actions are not added
      if (option.action) {
        return key !== lastActionType
      }

      const blockingOptions = options.filter((opt) => opt.blocking).map((opt) => opt.key)
      const stepHasBlockingAction = actionTypes.some((actionType) => blockingOptions.includes(actionType))

      if (!stepHasBlockingAction) {
        // there are some compatibility actions added which is not supported yet by builder
        // hence Extra check to not allow other options
        if (['message', 'guide', 'answer'].includes(lastActionType)) {
          return true
        }
      }

      return false
    },
    [step.actions, step.settings]
  )

  const canShowOptionByWidgetType = useCallback(
    (option) => {
      return option.allowedWidgets.includes(widget_type)
    },
    [widget_type]
  )

  const canShowOptionByIntegration = useCallback(
    (option) => {
      if (typeof option.integrationKey === 'undefined') {
        return true
      }

      if (!option.integrationKey) {
        return false
      }

      return apps.some((app) => app.app_type === option.integrationKey && app.is_active)
    },
    [apps]
  )

  const isAnyOptionVisible = useMemo(() => {
    return options.some((option) => {
      if (option.key === 'collect_user_question') {
        return false
      }
      return (
        isOptionAllowed(option) &&
        canShowOptionByFeature(option.featureKey) &&
        canShowOptionByIntegration(option) &&
        canShowOption(option) &&
        canShowOptionByWidgetType(option)
      )
    })
  }, [isOptionAllowed, canShowOptionByFeature, canShowOptionByIntegration, canShowOption, canShowOptionByWidgetType])

  const allowAddAction = useMemo(() => {
    const isStartStep = step.internal_name === 'start'

    switch (createActionType) {
      case 'botCollectInformation':
        if (isStartStep) {
          return false
        }
        return false

      case 'collect_user_question':
        return false

      default:
        return !showMenu
    }
  }, [createActionType, showMenu, step.internal_name])

  return (
    <React.Fragment>
      {createMode && (
        <div className='step-line'>
          <div className='quote-wrapper edit-mode'>
            <div className='quote-item'>
              <Editor {...defaultOptions} />
            </div>
          </div>
        </div>
      )}
      {isAnyOptionVisible && (
        <Fragment>
          {allowAddAction && (
            <div className='step-line action-button-wrapper'>
              <button className='add-action-button' onClick={onAddButtonClick}>
                <Plus />
              </button>
            </div>
          )}
          {showMenu && (
            <div className='step-line add_step_options_wrapper'>
              <ul className='options-list' ref={menuRef}>
                {options.map((option) => {
                  const isEnabled =
                    isOptionAllowed(option) &&
                    canShowOptionByFeature(option.featureKey) &&
                    canShowOptionByIntegration(option) &&
                    canShowOption(option) &&
                    canShowOptionByWidgetType(option)

                  if (!isEnabled) {
                    return null
                  }
                  return (
                    <ListItem key={option.key} onClick={() => onMenuClick(option)}>
                      {option.label}
                    </ListItem>
                  )
                })}
              </ul>
            </div>
          )}
        </Fragment>
      )}
    </React.Fragment>
  )
}

export default Options
